import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { GenericEventsRouteProvider } from '@wix/data-tools-generic-events';
import { AppShell } from '../../../components/AppShell';
import { PageHeader } from '../../../components/symphony/PageHeader';
import { ConnectorsBody } from '../../../components/symphony/connectors/ConnectorsBody';
import { AppsScriptSetup } from '../../../components/symphony/connectors/AppsScriptSetup';
import { ConnectorsGenome } from '../../../components/symphony/connectors/ConnectorsGenome';
import { ChannelsBody } from '../../../components/symphony/connectors/ChannelsBody';
import { ChannelsOutBody } from '../../../components/symphony/connectors/ChannelsOutBody';
import { ConnectorsMobile } from '../../../components/symphony/connectors/mobile/ConnectorsMobile';
import { WhatsappGroupScreen } from '../../../components/symphony/connectors/WhatsappGroupScreen';
import { PlusIcon } from '../../../components/symphony/connectors/icons';
import { Button, Card, Input, Tabs, Typography } from '../../../components/symphony/ui';
import { chatHandoffInPlace } from '../../../lib/chat-handoff';
import { useBasePath } from '../../../lib/use-base-path';
import { useNarrow } from '../../../lib/use-narrow';
import { useProductName } from '../../../lib/use-brand';
import { useTranslation } from '../../../lib/i18n';
import { useGenericBi } from '../../../lib/use-generic-bi';
import { CONNECTORS_PAGE, MCP_CONNECT_PROMPT, useAppsController } from '../../../lib/use-connectors';
import {
  CONNECTORS_BASE,
  CONNECTORS_TABS,
  connectorsPath,
  connectorsTargetElementId,
  parseConnectorsRoute,
  type ConnectorsTab,
} from '../../../lib/connectors-route';
import styles from './connectors.module.css';

const CONNECTORS_ROUTE = 'settings-connectors';

/**
 * Connectors — everything Symphony is wired to, in one page with two halves.
 *
 * **The URL is the state.** Which tab is showing, and which entry is being pointed at, are read from
 * the path and never from click state — that is what makes a cold open from a push notification
 * ("Gmail's access expired") behave identically to a click on the same row, and what survives the
 * reload at the end of a connector's OAuth round-trip. See `docs/connectors-page-design.md` §1.
 *
 * The catch-all is deliberate: `/settings/connectors`, `/…/{tab}` and `/…/{tab}/{slug}` are one page
 * with parameters, not three pages. A path that parses to something other than itself is normalized
 * with a `replace`, so the canonical form is what gets bookmarked and shared.
 */
export default function ConnectorsRoute() {
  return (
    <GenericEventsRouteProvider value={CONNECTORS_ROUTE}>
      <ConnectorsPage />
    </GenericEventsRouteProvider>
  );
}

function ConnectorsPage() {
  const { t } = useTranslation();
  const router = useRouter();
  const basePath = useBasePath();
  const narrow = useNarrow();
  const productName = useProductName();
  const { logPageClickBi } = useGenericBi();

  // One load for the whole page: the genome picks its nodes from the same list the Tools tab renders.
  // `useAppsController` also owns this page's load BI, so it must be called exactly once.
  const apps = useAppsController();

  // One search field lives in the control row and drives whichever tab is showing (Tools filters the
  // connector lists, Channels filters the channel sections). It resets on a tab switch — a query for a
  // connector says nothing about a channel section.
  const [query, setQuery] = useState('');
  const q = query.trim().toLowerCase();

  const slug = router.query.slug;
  const segments = Array.isArray(slug) ? slug : slug ? [slug] : [];
  const route = parseConnectorsRoute(segments);
  const canonical = connectorsPath(route);
  // Opening the MCP connect chat must NOT leave the Connectors page: land the handoff params on this
  // screen's own path (not the home route) so the floating chat dock expands over it in place.
  const connectHandoff = chatHandoffInPlace({ kind: 'new', message: MCP_CONNECT_PROMPT }, basePath, canonical);
  const current = segments.length ? `${CONNECTORS_BASE}/${segments.join('/')}` : CONNECTORS_BASE;

  // Normalize in place. A bare `/settings/connectors`, an unknown tab, or a link written without the
  // tab all land on a real state; rewriting the address bar to that state is what stops the next
  // reload or share from re-deriving it.
  useEffect(() => {
    if (router.isReady && current !== canonical) {
      router.replace(`${basePath}${canonical}`, undefined, { shallow: true });
    }
  }, [router.isReady, current, canonical, basePath]);

  // A deeplink's target (a push saying "Gmail's access expired", the map's outreach card) lands ON the
  // row: highlighted by the body, scrolled to here — once the tab it lives on has rendered.
  useEffect(() => {
    if (!route.target || route.whatsappGroupId) return;
    const el = document.getElementById(connectorsTargetElementId(route.tab, route.target));
    if (!el) return;
    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    el.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'center' });
  }, [route.tab, route.target, route.whatsappGroupId, apps.state.kind]);

  const selectTab = (tab: ConnectorsTab) => {
    if (tab === route.tab) return;
    logPageClickBi({ page_name: CONNECTORS_ROUTE, button_name: `tab-${tab}` });
    setQuery('');
    router.push(`${basePath}${connectorsPath({ tab })}`, undefined, { shallow: true });
  };

  // The one sub-route that is a SCREEN and not a scroll position: a group replaces the page rather
  // than rendering under the tabs, because that is what it is — and because two live server pushes
  // land straight on it (§4).
  if (route.whatsappGroupId) {
    return (
      <AppShell>
        <WhatsappGroupScreen groupId={route.whatsappGroupId} />
      </AppShell>
    );
  }

  if (route.tab === 'tools' && route.target === 'google-apps-script') {
    return <AppShell><AppsScriptSetup /></AppShell>;
  }

  // Mobile (≤719px) is a separate presentation of the same screen — the Figma 13131-78167 redesign —
  // consuming the same `apps` controller, route and channel sub-components. UI forks; logic does not.
  if (narrow) {
    return (
      <AppShell>
        <ConnectorsMobile apps={apps} route={route} initialTab={route.tab} onSelectTab={selectTab} />
      </AppShell>
    );
  }

  const isTools = route.tab === 'tools';
  const searchPlaceholder = isTools ? t('connectors.searchPlaceholder') : t('connectors.searchChannels');

  return (
    <AppShell>
      {/* A drill-in from Account — reached from its Connectors row and living under
          /settings — so it names its parent, exactly as WhatsApp and Agent models do. */}
      <PageHeader
        title={t('connectors.title')}
        description={t('connectors.description.symphony')}
        breadcrumb={[{ label: t('settings.title'), href: '/settings' }]}
      />

      {/* The animated hero, a full-width band above the tabs (Figma 14013-148717): channels flow in from
          the left, tools out to the right. Only its two "+N" chips act — they switch Tools / Channels. */}
      <Card variant="sunken" className={styles.genomeCard}>
        <ConnectorsGenome
          controller={apps}
          onBrowseTools={() => selectTab('tools')}
          onBrowseChannels={() => selectTab('channels')}
        />
      </Card>

      <div className={styles.controls}>
        {/* The DS Tabs Group at Size=L — the design's own component for this strip (Figma
            14013-149506 is an instance of it), so the metrics live in the primitive now. */}
        <Tabs
          items={CONNECTORS_TABS.map((tab) => ({ value: tab, label: t(`connectors.tab.${tab}`) }))}
          value={route.tab}
          onChange={selectTab}
          label={t('connectors.title')}
        />
        <div className={styles.controlsRight}>
          <Input
            className={`${styles.search} ${isTools ? styles.searchTools : styles.searchChannels}`}
            kind="search"
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onClear={() => setQuery('')}
            clearAriaLabel={t('search.dialog.clear')}
            placeholder={searchPlaceholder}
            aria-label={searchPlaceholder}
          />
          {isTools && (
            <Button
              href={connectHandoff.route}
              linkAs={connectHandoff.path}
              size="large"
              startIcon={<PlusIcon size={16} />}
              onClick={() => logPageClickBi({ ...CONNECTORS_PAGE, button_name: 'connect_mcp' })}
            >
              {t('connectors.mcp.connect')}
            </Button>
          )}
        </div>
      </div>

      <Typography className={styles.subtitle} variant="p2" color="secondary">
        {isTools ? t('connectors.section.subtitle') : route.tab === 'channels-out' ? t('connectors.channelsOut.subtitle', { productName }) : t('connectors.section.subtitleChannels', { productName })}
      </Typography>

      {/* Full-width sections, one column. Tools: Available / Connected / MCP. Channels: the channels body. */}
      {isTools ? (
        <ConnectorsBody apps={apps} query={q} connectHandoff={connectHandoff} />
      ) : route.tab === 'channels-out' ? (
        <ChannelsOutBody target={route.target} query={q} />
      ) : (
        <ChannelsBody target={route.target} query={q} />
      )}
    </AppShell>
  );
}
